import { Fragment } from 'react';
import { Check, X } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  PACKAGES,
  PACKAGE_FEATURE_MATRIX,
  PACKAGE_ORDER,
  formatPackageName
} from '../../data/seoPackages';

function renderCellValue(value) {
  if (value === true) {
    return <Check className="w-4 h-4 text-emerald-600 mx-auto" />;
  }

  if (value === false) {
    return <X className="w-4 h-4 text-slate-400 mx-auto" />;
  }

  return <span className="text-sm">{value}</span>;
}

export default function SeoPackagesCompare({
  currentPackageId = null,
  onSubscribe = null,
  title = 'Compare SEO Packages',
  subtitle = 'Explore full deliverables across all plans.',
  showHeader = true
}) {
  return (
    <Card className="gds-card">
      <CardContent className="p-0">
        {showHeader && (
          <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">
            <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{title}</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">{subtitle}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="sticky top-0 z-10 bg-white dark:bg-slate-950">
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <th className="text-left p-4 font-semibold text-slate-700 dark:text-slate-200 w-[34%]">
                  Features
                </th>
                {PACKAGE_ORDER.map((packageId) => {
                  const pkg = PACKAGES.find((item) => item.id === packageId);
                  const isCurrent = currentPackageId === packageId;
                  return (
                    <th
                      key={packageId}
                      className={`text-center p-4 align-top ${
                        isCurrent ? 'bg-emerald-50/60 dark:bg-emerald-900/20' : ''
                      }`}
                    >
                      <div className="font-heading text-lg font-bold text-slate-900 dark:text-white">
                        {pkg?.name || formatPackageName(packageId)}
                      </div>
                      <div className="text-sm text-slate-500 mt-1">${pkg?.price}/month</div>
                      <div className="mt-3">
                        {isCurrent ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-500 text-white text-xs px-3 py-1">
                            Current Plan
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            className={pkg?.popular ? 'gds-btn-primary' : 'gds-btn-secondary'}
                            onClick={() => onSubscribe?.(packageId)}
                            disabled={!onSubscribe}
                          >
                            Subscribe
                          </Button>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {PACKAGE_FEATURE_MATRIX.map((group) => (
                <Fragment key={group.section}>
                  <tr key={`${group.section}-header`} className="bg-slate-50 dark:bg-slate-900/50">
                    <td
                      colSpan={5}
                      className="px-4 py-3 text-sm font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-300"
                    >
                      {group.section}
                    </td>
                  </tr>
                  {group.rows.map((row) => (
                    <tr
                      key={`${group.section}-${row.feature}`}
                      className="border-b border-slate-100 dark:border-slate-800"
                    >
                      <td className="p-4 text-sm text-slate-700 dark:text-slate-300">{row.feature}</td>
                      {PACKAGE_ORDER.map((packageId) => (
                        <td
                          key={`${row.feature}-${packageId}`}
                          className={`p-4 text-center text-slate-700 dark:text-slate-300 ${
                            currentPackageId === packageId ? 'bg-emerald-50/40 dark:bg-emerald-900/10' : ''
                          }`}
                        >
                          {renderCellValue(row.values[packageId])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
